import { DashboardProject, DefaultRoles, FeatureManagementType, Permissions } from "@api/apiClient";
import { useExportMultiProjectData } from "@api/analysis";
import { useApsSyncUsers } from "@api/apsUsers";
import { useDashboardProjects } from "@api/projects";
import useAppStore from "@hooks/useAppStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import GridViewIcon from "@mui/icons-material/GridView";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SyncIcon from "@mui/icons-material/Sync";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { Button, CircularProgress, Grid } from "@mui/material";

// Maximum number of projects that can be selected for multi-project export
const MAX_PROJECT_SELECTION = 10;
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ProgressIndicatorType } from "@/types/ProgressIndicator";
import { observer } from "mobx-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import AddProjectForm from "../AddProjectForm";
import DashboardGrid from "../DashboardGrid";
import DashboardTiles from "../DashboardTiles";

const DashboardPage = observer(() => {
  const theme = useTheme();
  const appStore = useAppStore();
  const [view, setView] = useState("listView");
  const [searchedDashboardData, setSearchedDashboardData] = useState<DashboardProject[]>([]);
  const [openTable, setOpenTable] = useState(false);
  const dashboardProjects = useDashboardProjects();
  const upStore = useUserPermissionStore();
  const apsUserSync = useApsSyncUsers();
  const [isMyProjectFilterActive, setIsMyProjectFilterActive] = useState(false);
  const isAdmin = upStore.userPermission?.roles.includes(DefaultRoles.Admin) ?? false;
  
  // Multi-project export state
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const exportMultiProjectData = useExportMultiProjectData();
  
  // Check if selection limit is reached
  const isSelectionLimitReached = selectedProjects.length >= MAX_PROJECT_SELECTION;
  const handleClickOpenTable = () => {
    setOpenTable(true);
  };

  const handleCloseTable = () => {
    setOpenTable(false);
  };

  const handleRefreshClick = async () => {
    try {
      appStore.setIsLoading(true);
      await dashboardProjects.refetch();
    } finally {
      appStore.setIsLoading(false);
    }
  };
  const handleSyncUsersClick = async () => {
    apsUserSync.mutateAsync();
  };
  const handleInputChange = (event: any) => {
    searchProjects(event.target.value);
  };

  const ProjectFilterToggle = async () => {
    setIsMyProjectFilterActive((prev) => !prev);
    if (!isMyProjectFilterActive) {
      handleSearchMyProject();
    } else {
      searchProjects("");
    }
  };

  const handleSearchMyProject = () => {
    setSearchedDashboardData(dashboardProjects.data?.filter((e) => e?.createdBy === appStore?.userProfile?.userId) || []);
  };

  function searchProjects(searchedVal: string) {
    const search = searchedVal ? searchedVal.trim().toLowerCase() : "";
    setSearchedDashboardData(dashboardProjects.data?.filter((e) => e?.name?.toLowerCase().includes(search)) || []);
  }

  useEffect(() => {
    searchProjects("");
  }, [dashboardProjects.data]);

  const handleExportResults = async () => {
    try {
      const response = await exportMultiProjectData.mutateAsync(selectedProjects);
      
      if (response && response.jobId) {
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.MultiProjectExport, true);
      }
    } catch (error) {
      console.error("Error exporting multi-project data:", error);
    }
  };

  const handleSelectionChange = (newSelection: string[]) => {
    setSelectedProjects(newSelection);
  };

  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState<number>(400);

  const updateGridHeight = useCallback(() => {
    if (gridContainerRef.current) {
      const top = gridContainerRef.current.getBoundingClientRect().top;
      const footerHeight = 40;
      setGridHeight(window.innerHeight - top - footerHeight);
    }
  }, []);

  useLayoutEffect(() => {
    updateGridHeight();
  }, [updateGridHeight]);

  useEffect(() => {
    window.addEventListener("resize", updateGridHeight);
    return () => window.removeEventListener("resize", updateGridHeight);
  }, [updateGridHeight]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, flexShrink: 1 }}>
       <Box sx={{ width: `100vw` }}>
        <Container maxWidth={false}>         
            <Box mt={2}>
              <Typography>{i18n.t("project-dashboard-page.header")}</Typography>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Tooltip title={i18n.t("project-dashboard-page.my-projects-button.tooltip")}>
                  <Box component="span">
                    <IconButton color={isMyProjectFilterActive ? "primary" : "default"} onClick={ProjectFilterToggle} sx={{ position: "relative" }}>
                      <PersonOutlineIcon />
                      <FilterAltIcon sx={{ position: "absolute", top: 8, right: 4, fontSize: "small", background: theme.palette.background.default }} />
                    </IconButton>
                  </Box>
                </Tooltip>
                <Tooltip title={i18n.t("project-dashboard-page.list-view-button.tooltip")}>
                  <IconButton onClick={() => setView("listView")}>
                    <TableRowsIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("project-dashboard-page.tile-view-button.tooltip")}>
                  <IconButton onClick={() => setView("tileView")}>
                    <GridViewIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("project-dashboard-page.refresh-button.tooltip")}>
                  <IconButton onClick={handleRefreshClick}>
                    <SyncIcon />
                  </IconButton>
                </Tooltip>
                {appStore.hasFeature(FeatureManagementType.ShareProject) && upStore.hasPermission(Permissions.App_Project_Create) && (
                  <Tooltip title={i18n.t("project-dashboard-page.aps-user-sync-button.tooltip")}>
                    <IconButton onClick={handleSyncUsersClick}>
                      <CloudSyncIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={i18n.t("project-dashboard-page.add-project-button.tooltip")}>
                  <Box component="span">
                    <IconButton onClick={handleClickOpenTable} disabled={!upStore.hasPermission(Permissions.App_Project_Create)}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
                <TextField
                  variant="standard"
                  size="small"
                  onChange={handleInputChange}
                  onBlur={function noRefCheck() {}}
                  onFocus={function noRefCheck() {}}
                  onMouseEnter={function noRefCheck() {}}
                  onMouseLeave={function noRefCheck() {}}
                  label={i18n.t("project-dashboard-page.search")}
                  sx={{ marginLeft: 2, marginBottom: 2 }}
                />
                {isAdmin && (
                  <Tooltip title={i18n.t("dashboard.multi-export.export-results-button.tooltip")}>
                    <Box component="span">
                      <Button
                        variant="contained"
                        startIcon={exportMultiProjectData.isPending || appStore.progressIndicator.getShowProgressIndicator(ProgressIndicatorType.MultiProjectExport) ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
                        onClick={handleExportResults}
                        disabled={selectedProjects.length === 0 || exportMultiProjectData.isPending || appStore.progressIndicator.getShowProgressIndicator(ProgressIndicatorType.MultiProjectExport)}
                        sx={{ marginLeft: 2, marginBottom: 2 }}
                      >
                        {i18n.t("dashboard.multi-export.export-results-button.label")}
                        {selectedProjects.length > 0 && (
                          <Box component="span" sx={{ ml: 1 }}>
                            ({i18n.t("dashboard.multi-export.selection-counter", { count: selectedProjects.length, max: MAX_PROJECT_SELECTION })})
                          </Box>
                        )}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Box ref={gridContainerRef} sx={{ height: gridHeight, overflow: "hidden", mt: 1 }}>
              {view && view === "tileView" && (
                <DashboardTiles 
                  data={searchedDashboardData}
                  selectedProjects={selectedProjects}
                  onSelectionChange={handleSelectionChange}
                  isSelectionLimitReached={isSelectionLimitReached}
                  enableSelection={isAdmin}
                />
              )}
              {view && view === "listView" && (
                <DashboardGrid 
                  data={searchedDashboardData} 
                  handleRefreshClick={handleRefreshClick}
                  selectedProjects={selectedProjects}
                  onSelectionChange={handleSelectionChange}
                  isSelectionLimitReached={isSelectionLimitReached}
                  enableSelection={isAdmin}
                />
              )}
            </Box>
            <Dialog fullWidth={true} maxWidth={"sm"} open={openTable} onClose={handleCloseTable} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
              <DialogTitle
                id="alert-dialog-title"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {i18n.t("project-dashboard-page.add-new-project")}
                <IconButton title={i18n.t("project-dashboard-page.close-button.title")} onClick={handleCloseTable}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ height: "40%" }}>
                <DialogContentText id="alert-dialog-description"></DialogContentText>
                <AddProjectForm existingProjects={dashboardProjects.data} handleCreateProject={handleRefreshClick} handleCloseDialog={handleCloseTable} />
              </DialogContent>
            </Dialog>      
        </Container>
      </Box>
    </Box>
  );
});

export default DashboardPage;
